import { initChannelTalk } from '@/apps/client/libs/initChannelTalk';
import { SERVICE, SERVICE_DOMAIN, SERVICE_PORT, SERVICE_PROTOCOL } from '@/libs/constant';
import { requester } from '@/libs/requester';
import { LANG } from '@/libs/translation';
import { updateResizeS3Image } from '@/libs/util/resizeS3Image';
import { Modal, ServicePopup } from '@/ui/component';
import { checkImageFormat, initLazyLoad, type SharedData } from '@/ui/util';
import { each, entries, join, map, pipe } from '@fxts/core';
import { mauth } from '@marpple/auth';
import { HydratedEvent } from '@rune-ts/server';
import * as Sentry from '@sentry/browser';
import { isAxiosError } from 'axios';
import { $, Page } from 'rune-ts';

export class Application {
  page!: Page<object>;
  props!: {
    data: object;
    args: object[];
    sharedData: SharedData;
    name: string;
  };
  el!: HTMLElement;

  getInitialData() {
    const script = document.querySelector('script.__RUNE_DATA__[data-rune-base-name="Page"]');
    if (!script) {
      throw new Error('No __RUNE_DATA__ script found');
    }

    this.props = JSON.parse(script.textContent!);

    const el = $(script)?.prev('[data-rune]')?.element();

    if (!el) {
      throw new Error('No RUNE VIEW found');
    }

    this.el = el;

    script.remove();

    return this;
  }

  async hydrate() {
    const _router = (await import(`@/ui/component/page/${this.props.name}/${this.props.name}`))[this.props.name];
    const client = new _router(this.props.data, this.props.sharedData, ...this.props.args);

    const page = client.hydrateFromSSR(this.el);
    page.dispatchEvent(HydratedEvent, { detail: page });
    this.page = page;

    return this;
  }

  setRequesterAxios() {
    /** @description 브라우저에 있는 client_id를 활용하기 위한 axios 세팅*/
    const client_id = this.props.sharedData.client_id;

    if (client_id) {
      requester.defaults.headers.common['client-id'] = client_id;
      requester.defaults.params = { client_id };
    }

    return this;
  }

  private snsLogin(data: { type: string } & Record<string, string>) {
    const type = data.type.split('#')[0];

    if (type) {
      location.href = `/oauth/${type.toLowerCase()}?${pipe(
        { ...data, state: 'success' },
        entries,
        map(([key, value]) => `${key}=${value}`),
        join('&'),
      )}`;
    }
  }

  private appListener(e: any) {
    try {
      const app_sns_login_data = JSON.parse(e.data);

      switch (app_sns_login_data.type) {
        case 'SNS_LOGIN':
          this.snsLogin(app_sns_login_data.data);
          break;
        default:
          break;
      }
    } catch (_) {
      return;
    }
  }

  setAppListener() {
    /** @description 마플샵 앱을 sns 로그인을 위한 postmessage 이벤트 핸들러 등록*/
    window.addEventListener('message', this.appListener.bind(this));
    document.addEventListener('message', this.appListener.bind(this));

    return this;
  }

  initLazyLoad() {
    /** @description 이미지 lazy 로딩 지원*/
    initLazyLoad();

    return this;
  }

  checkImageFormat() {
    /** @description 이미지 지원안하는 경우 png로 변경*/
    checkImageFormat(updateResizeS3Image);

    return this;
  }

  setSentryConfig() {
    const user = mauth.getUser();

    if (user) {
      Sentry.setUser({
        id: user.uid,
        email: user.email,
        username: user.nickname,
      });
    }
    Sentry.setContext('page_name', this.props.name);
    Sentry.setContext('page_data', this.props.data);
    Sentry.setContext('page_sharedData', this.props.sharedData);

    return this;
  }

  openPopup() {
    if (this.props.sharedData.popup_data?.length) {
      pipe(this.props.sharedData.popup_data, each(ServicePopup.open));
    }

    return this;
  }

  closeModal() {
    const modal = Modal.find();
    if (modal) {
      modal.close('close');
    }

    return this;
  }

  async initChannelTalk() {
    if (this.props.sharedData.skip_channel_talk) {
      return this;
    }

    if (this.props.sharedData.theme === SERVICE.Ciety) {
      return this;
    }

    await import('./libs/channelTalkScript');
    initChannelTalk(this.page);
    return this;
  }

  async setMauth() {
    try {
      await mauth.init({
        client_id: process.env.MCO_CLIENT_ID!,
        lang: LANG,
        version: 1,
        host: SERVICE_PROTOCOL + SERVICE_DOMAIN + SERVICE_PORT,
      });
    } catch (e) {
      if (isAxiosError(e)) {
        Sentry.setContext('response', e.response);
        Sentry.setContext('request', e.request);
        Sentry.setContext('headers', e.response?.headers);
      }
      Sentry.captureException(e);
    }

    mauth.addEventListener('Logout', () => {
      return location.reload();
    });

    mauth.addEventListener('DifferentUser', () => {
      return location.reload();
    });

    mauth.addEventListener('UserUpdate', () => {
      return location.reload();
    });

    return this;
  }
}
